/**
 * Guest name generation for the WebRTC conferencing app.
 *
 * Exports curated word lists and a helper that produces a unique
 * friendly display name of the form "<Adjective> <Noun>", checking
 * uniqueness via an `isTaken` predicate supplied by the caller.
 *
 * - Server: pass `name => room.hasParticipantNamed(name)`
 * - Client preview: pass `() => false`
 */

// ---------------------------------------------------------------------------
// Word lists
// ---------------------------------------------------------------------------

export const ADJECTIVES: string[] = [
  'Brave', 'Calm', 'Clever', 'Daring', 'Eager',
  'Fancy', 'Gentle', 'Happy', 'Honest', 'Jolly',
  'Kind', 'Lively', 'Lucky', 'Merry', 'Mighty',
  'Noble', 'Peaceful', 'Plucky', 'Proud', 'Quick',
  'Radiant', 'Serene', 'Shiny', 'Silent', 'Silly',
  'Sleepy', 'Smart', 'Snappy', 'Speedy', 'Spirited',
  'Sprightly', 'Stellar', 'Sunny', 'Swift', 'Tender',
  'Thoughtful', 'Vivid', 'Warm', 'Witty', 'Zesty',
]

export const NOUNS: string[] = [
  'Otter', 'Falcon', 'Maple', 'Panda', 'Robin',
  'Cedar', 'Ember', 'Finch', 'Harbor', 'Ivy',
  'Jasper', 'Kite', 'Lark', 'Lotus', 'Luna',
  'Meadow', 'Minnow', 'Moss', 'Mulberry', 'Nebula',
  'Opal', 'Orchid', 'Pearl', 'Pebble', 'Pine',
  'Quail', 'Raven', 'Reed', 'Reef', 'Ridge',
  'River', 'Sage', 'Sparrow', 'Spruce', 'Stone',
  'Stream', 'Summit', 'Swift', 'Thistle', 'Willow',
]

// ---------------------------------------------------------------------------
// Generation helper
// ---------------------------------------------------------------------------

/**
 * Generate a unique guest display name.
 *
 * Resolution order (always terminates for any finite taken-set):
 *   1. Random fast path — try a bounded number of random
 *      `"<Adjective> <Noun>"` combinations (great for sparse rooms, gives
 *      name variety).
 *   2. Deterministic exhaustive scan — if the random phase didn't land a free
 *      name, walk the full Adjective × Noun space and return the first free
 *      combination. This GUARANTEES any free base combination is found (the
 *      random phase alone can miss a lone free name, which previously let the
 *      suffix fallback below loop forever).
 *   3. Numeric-suffix fallback — only when every base combination is taken,
 *      append an incrementing suffix (`"<base> 2"`, `" 3"`, …) until free.
 *
 * Always returns a non-empty string.
 *
 * @param isTaken - Predicate that returns `true` if the candidate name is
 *                  already in use. Callers control uniqueness scope.
 */
export function generateGuestName(isTaken: (name: string) => boolean): string {
  const combinationCount = ADJECTIVES.length * NOUNS.length
  // Cap random attempts at twice the combination count: good coverage for
  // sparse rooms without spinning when the space is dense.
  const maxAttempts = combinationCount * 2

  // 1. Random fast path.
  for (let i = 0; i < maxAttempts; i++) {
    const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)]
    const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)]
    const candidate = `${adj} ${noun}`
    if (!isTaken(candidate)) {
      return candidate
    }
  }

  // 2. Deterministic exhaustive scan — guarantees finding ANY free base
  //    combination (the random phase can miss a single free name).
  for (const adj of ADJECTIVES) {
    for (const noun of NOUNS) {
      const candidate = `${adj} ${noun}`
      if (!isTaken(candidate)) {
        return candidate
      }
    }
  }

  // 3. Every base combination is taken — append a numeric suffix to a base.
  const base = `${ADJECTIVES[0]} ${NOUNS[0]}`
  for (let suffix = 2; ; suffix++) {
    const candidate = `${base} ${suffix}`
    if (!isTaken(candidate)) {
      return candidate
    }
  }
}
